import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { CohortFields } from './CohortFields';
import { targetsSummary } from './targetsSummary';
import { useDebouncedValue } from './useDebouncedValue';
import {
  useProactivityExperimentCohortPreview,
  useUpdateProactivityExperimentCohort,
} from './useProactivityExperiments';

import type {
  ProactivityExperimentCohortMember,
  ProactivityExperimentCohortPreview,
  ProactivityExperimentDetail,
  ProactivityExperimentOptions,
  UpdateProactivityExperimentCohortInput,
} from './proactivity-experiments-types';
import type { UseQueryResult } from '@tanstack/react-query';

const PREVIEW_DEBOUNCE_MS = 400;

const MemberList = ({
  label,
  members,
}: {
  label: string;
  members: ProactivityExperimentCohortMember[];
}) => {
  if (members.length === 0) return <span>{label} 0</span>;

  return (
    <details>
      <summary className="cursor-pointer">
        {label} {members.length}
      </summary>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto pl-4">
        {members.map((member) => (
          <li key={member.id} className="break-all text-muted-foreground">
            {member.name ? `${member.name} · ` : ''}
            {member.email}
          </li>
        ))}
      </ul>
    </details>
  );
};

const CohortChangePreview = ({
  preview,
  stale,
}: {
  preview: UseQueryResult<ProactivityExperimentCohortPreview>;
  stale: boolean;
}) => {
  if (stale || preview.isPending) {
    return (
      <p className="text-muted-foreground">
        Checking who this change affects...
      </p>
    );
  }
  if (preview.error) {
    return (
      <p role="alert" className="text-destructive">
        {preview.error.message}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <MemberList label="Adds" members={preview.data.added} />
      <MemberList label="Drops" members={preview.data.dropped} />
    </div>
  );
};

type ProactivityExperimentCohortDialogProps = {
  experiment: ProactivityExperimentDetail;
  options: ProactivityExperimentOptions;
  onOpenChange: (open: boolean) => void;
};

export const ProactivityExperimentCohortDialog = ({
  experiment,
  options,
  onOpenChange,
}: ProactivityExperimentCohortDialogProps) => {
  const updateCohort = useUpdateProactivityExperimentCohort(experiment.id);
  const [cohortValue, setCohortValue] = useState(
    experiment.cohort ? String(experiment.cohort.id) : ''
  );
  const [additionalEmails, setAdditionalEmails] = useState(
    experiment.additional_emails
  );
  const selectedCohort = options.cohorts.find(
    (cohort) => String(cohort.id) === cohortValue
  );
  const hasRecipients =
    selectedCohort !== undefined || additionalEmails.length > 0;
  const staleCohort =
    experiment.cohort !== null &&
    !options.cohorts.some((cohort) => cohort.id === experiment.cohort?.id);

  const input: UpdateProactivityExperimentCohortInput = {
    cohort_id: selectedCohort?.id ?? null,
    additional_emails: additionalEmails,
  };
  const previewBody = JSON.stringify(input);
  const debouncedPreviewBody = useDebouncedValue(
    previewBody,
    PREVIEW_DEBOUNCE_MS
  );
  const preview = useProactivityExperimentCohortPreview(
    experiment.id,
    debouncedPreviewBody,
    hasRecipients
  );
  // Never save against a count the admin has not seen for the current input
  const previewStale =
    debouncedPreviewBody !== previewBody || preview.isFetching;

  const handleOpenChange = (open: boolean) => {
    if (updateCohort.isPending) return;
    onOpenChange(open);
  };

  const handleSave = () =>
    updateCohort.mutate(input, { onSuccess: () => onOpenChange(false) });

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="space-y-1.5">
          <DialogTitle>Edit cohort</DialogTitle>
          <DialogDescription>
            Change who {experiment.name} targets. The card copy and prompt stay
            as they are.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <CohortFields
            cohorts={options.cohorts}
            cohortValue={cohortValue}
            onCohortValueChange={setCohortValue}
            emails={additionalEmails}
            onEmailsChange={setAdditionalEmails}
          />
          {staleCohort && experiment.cohort ? (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Cohort {experiment.cohort.name} is no longer available. Pick a
              replacement.
            </p>
          ) : null}
        </div>

        <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
          <p>{targetsSummary(selectedCohort, additionalEmails.length)}</p>
          {hasRecipients ? (
            <CohortChangePreview preview={preview} stale={previewStale} />
          ) : null}
          <p className="text-amber-700 dark:text-amber-400">
            Saving re-reads the PostHog cohort and reconciles. Users no longer
            targeted lose their card if they haven't opened it; new users get
            one. Saving without changes still applies whatever the cohort looks
            like now.
          </p>
        </div>

        {updateCohort.error ? (
          <p role="alert" className="text-sm text-destructive">
            {updateCohort.error.message}
          </p>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              disabled={updateCohort.isPending}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={
              updateCohort.isPending ||
              !hasRecipients ||
              previewStale ||
              preview.isError ||
              !preview.data
            }
            onClick={handleSave}
          >
            {updateCohort.isPending ? 'Saving...' : 'Save cohort'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
